import {
  ArrowDownSmIcon,
  DropdownMenuMulti,
  DropdownMenuMultiContent,
  DropdownMenuMultiItem,
  DropdownMenuMultiSelectAllItem,
  DropdownMenuTrigger,
  RewriteMdIcon,
} from '@crono-one/design-system';
import EnrichRow, {
  EnrichRowCredits,
} from 'components/CreationForms/base/EnrichRow';
import { hasNoCreditsLeft } from 'components/CreationForms/utils/credits';
import { TableType } from 'crono-fe-common/types/externalProperty';
import useGetExternalProperty from 'hooks/services/externalProperty/useGetExternalProperty';

const getTitle = (selectedCount: number) =>
  selectedCount > 0
    ? `AI field${selectedCount !== 1 ? 's' : ''} (${selectedCount})`
    : 'AI fields';

const credits: EnrichRowCredits = { type: 'rewrite', showOnlyLeft: true };

export interface AiFieldsEnrichPickerProps {
  tableType: TableType;
  /** Shown under the title. */
  subtitle?: string;
  /** AI credits left: without any, no field can be picked. */
  creditsLeft: number | null | undefined;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}

/**
 * AI fields picker of the creation forms: picks the AI fields generated for the
 * new records. It shows the credits left while a field is picked, or when none
 * are left to explain why nothing can be. Renders nothing when the table type
 * has no AI fields.
 */
const AiFieldsEnrichPicker = ({
  tableType,
  subtitle,
  creditsLeft,
  selectedIds,
  onChange,
}: AiFieldsEnrichPickerProps) => {
  const { data } = useGetExternalProperty({ tableType, onlyAiVariables: true });

  const aiFields = data?.data?.data ?? [];
  if (aiFields.length === 0) {
    return null;
  }

  const selectedCount = selectedIds.length;
  const noCreditsLeft = hasNoCreditsLeft(creditsLeft);
  const showCredits = selectedCount > 0 || noCreditsLeft;

  return (
    <DropdownMenuMulti value={selectedIds} onValueChange={onChange}>
      {/* A div, not the default <button>: the app has no preflight, so a
          button would bring its native font, alignment and colours along. */}
      <DropdownMenuTrigger
        nativeButton={false}
        render={(props) => (
          <EnrichRow
            {...props}
            className="group cursor-pointer outline-hidden hover:bg-neutral-100 focus-visible:ring-3 focus-visible:ring-primary-700/50 data-popup-open:bg-neutral-100"
            icon={RewriteMdIcon}
            title={getTitle(selectedCount)}
            subtitle={subtitle}
            credits={showCredits ? credits : undefined}
            end={
              <ArrowDownSmIcon className="size-4 text-gray-700 group-data-popup-open:rotate-180" />
            }
          />
        )}
      />
      <DropdownMenuMultiContent className="max-h-80 w-(--anchor-width) overflow-auto">
        <DropdownMenuMultiSelectAllItem
          values={aiFields.map((field) => field.id)}
          disabled={noCreditsLeft}
        >
          Select All
        </DropdownMenuMultiSelectAllItem>
        {aiFields.map((field) => (
          <DropdownMenuMultiItem
            key={field.id}
            value={field.id}
            disabled={noCreditsLeft}
          >
            <span className="font-medium text-gray-900">
              {field.publicName}
            </span>
          </DropdownMenuMultiItem>
        ))}
      </DropdownMenuMultiContent>
    </DropdownMenuMulti>
  );
};

export default AiFieldsEnrichPicker;
